// src/modules/feature-flags.js — Feature Flags admin page (per-office/profile management)
import { escapeHtml, showToast } from '../utils/dom.js';
import { logger, apiFetchWithRefresh } from './debug.js';

let ffData = null; // { offices: [], globalDefaults: {} }
let ffSaveTimers = {}; // emailHash → timeout id for fade-out

// Section keys for analytics flag sections
const SECTION_KEYS = [
    { key: 'noteCount', label: 'Note Count' },
    { key: 'timeSaved', label: 'Time Saved' },
    { key: 'costSavings', label: 'Cost Savings' },
    { key: 'recordingActivityChart', label: 'Recording Activity Chart' },
    { key: 'timeSavedChart', label: 'Time Saved Chart' },
    { key: 'perDoctorBreakdown', label: 'Per Doctor Breakdown' },
    { key: 'todaySummary', label: 'Today Summary' },
    { key: 'notesReview', label: 'Notes Review' },
    { key: 'peakHours', label: 'Peak Hours' },
    { key: 'heatmap', label: 'Heatmap' },
    { key: 'appointmentTypeStats', label: 'Appointment Type Stats' },
];

// Display labels for known simple flags; unknown flags get auto-derived labels
const FLAG_LABELS = {
    quickNote: 'Quick Note',
    consentForm: 'Consent Form',
    deepgramRecording: 'Deepgram Recording',
};

function getFlagLabel(key) {
    return FLAG_LABELS[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, function(s) { return s.toUpperCase(); }).trim();
}

/**
 * Extract simple boolean flags from a flags object.
 * A "simple flag" is any top-level key (not 'analytics', not 'level') with shape { enabled: boolean }.
 */
function getSimpleFlags(flagsObj) {
    const simple = [];
    for (const [key, val] of Object.entries(flagsObj || {})) {
        if (key === 'analytics' || key === 'level') continue;
        if (val && typeof val === 'object' && 'enabled' in val) {
            simple.push({ key, enabled: !!val.enabled });
        }
    }
    return simple;
}

export async function initFeatureFlags() {
    const loading = document.getElementById('ffLoading');
    const error = document.getElementById('ffError');
    const officeList = document.getElementById('ffOfficeList');
    const globalCard = document.getElementById('ffGlobalDefaults');

    if (loading) loading.classList.remove('hidden');
    if (error) error.classList.add('hidden');
    if (officeList) officeList.classList.add('hidden');

    try {
        const res = await apiFetchWithRefresh('/admin/feature-flags/offices', { method: 'GET' });
        if (!res.ok) {
            const errData = await res.json().catch(() => ({}));
            throw new Error(errData.error || 'Failed to load feature flags');
        }
        ffData = await res.json();
        renderGlobalDefaults(ffData.globalDefaults, globalCard);
        renderOfficeList(ffData.offices, officeList);
        if (loading) loading.classList.add('hidden');
        if (officeList) officeList.classList.remove('hidden');
    } catch (err) {
        logger.error('FEATURE_FLAGS', 'Failed to load feature flags offices', err, { context: 'initFeatureFlags' });
        if (loading) loading.classList.add('hidden');
        if (error) error.classList.remove('hidden');
        const errMsg = document.getElementById('ffErrorMessage');
        if (errMsg) errMsg.textContent = err.message || 'Failed to load data.';
    }
}

export function cleanupFeatureFlags() {
    ffData = null;
    Object.values(ffSaveTimers).forEach(clearTimeout);
    ffSaveTimers = {};
    const officeList = document.getElementById('ffOfficeList');
    if (officeList) officeList.innerHTML = '';
    const globalCard = document.getElementById('ffGlobalDefaults');
    if (globalCard) globalCard.innerHTML = '';
}

function renderGlobalDefaults(defaults, container) {
    if (!container || !defaults) return;
    const analytics = defaults.analytics || {};
    const sections = analytics.sections || {};
    const simpleFlags = getSimpleFlags(defaults);

    let html = '<p class="text-muted small mb-2">These are the global defaults applied to offices without custom overrides.</p>';
    html += '<div class="row g-2">';
    html += '<div class="col-sm-4"><span class="badge ' + (analytics.enabledForOfficeAdmin ? 'bg-success' : 'bg-secondary') + '">Analytics (Admin): ' + (analytics.enabledForOfficeAdmin ? 'ON' : 'OFF') + '</span></div>';
    html += '<div class="col-sm-4"><span class="badge ' + (analytics.enabledForDoctor ? 'bg-success' : 'bg-secondary') + '">Analytics (Doctor): ' + (analytics.enabledForDoctor ? 'ON' : 'OFF') + '</span></div>';

    // Render all simple flags dynamically
    simpleFlags.forEach(function(f) {
        html += '<div class="col-sm-4"><span class="badge ' + (f.enabled ? 'bg-success' : 'bg-secondary') + '">' + escapeHtml(getFlagLabel(f.key)) + ': ' + (f.enabled ? 'ON' : 'OFF') + '</span></div>';
    });
    html += '</div>';

    // Show sections
    const enabledSections = SECTION_KEYS.filter(s => sections[s.key] !== false);
    if (enabledSections.length > 0) {
        html += '<div class="mt-2"><small class="text-muted">Sections: ' + enabledSections.map(s => escapeHtml(s.label)).join(', ') + '</small></div>';
    }
    container.innerHTML = html;
}

function renderOfficeList(offices, container) {
    if (!container) return;
    if (!offices || offices.length === 0) {
        container.innerHTML = '<div class="alert alert-info">No offices found.</div>';
        return;
    }

    container.innerHTML = offices.map(office => renderOfficeCard(office)).join('');

    // Wire event listeners for all office cards
    offices.forEach(office => {
        wireOfficeCardEvents(office);
    });
}

function getLevelBadgeClass(level) {
    if (level === 'office') return 'bg-primary';
    if (level === 'profile') return 'bg-info';
    return 'bg-secondary';
}

function getLevelLabel(level) {
    if (level === 'office') return 'Office Level';
    if (level === 'profile') return 'Profile Level';
    return 'Global';
}

function renderOfficeCard(office) {
    const hash = office.emailHash;
    const flags = office.flags || {};
    const analytics = flags.analytics || {};
    const sections = analytics.sections || {};
    const level = flags.level || 'global';
    const profiles = office.profiles || [];
    const profileFlags = office.profileFlags || {};

    let html = '<div class="card mb-3" id="ff-card-' + escapeHtml(hash) + '">';
    html += '<div class="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">';
    html += '<div>';
    html += '<strong>' + escapeHtml(office.clinicName || 'Unknown Office') + '</strong>';
    html += '<small class="text-muted ms-2">' + escapeHtml(office.email || '') + '</small>';
    html += '</div>';
    html += '<span class="badge ' + getLevelBadgeClass(level) + '" id="ff-badge-' + escapeHtml(hash) + '">' + getLevelLabel(level) + '</span>';
    html += '</div>';
    html += '<div class="card-body">';

    // Level selector (only show if profiles exist)
    if (profiles.length > 0 && office.profilesEnabled) {
        html += '<div class="mb-3">';
        html += '<div class="btn-group btn-group-sm" role="group" aria-label="Flag level for ' + escapeHtml(office.clinicName || '') + '">';
        html += '<input type="radio" class="btn-check" name="ff-level-' + escapeHtml(hash) + '" id="ff-level-office-' + escapeHtml(hash) + '" ' + (level !== 'profile' ? 'checked' : '') + '>';
        html += '<label class="btn btn-outline-primary" for="ff-level-office-' + escapeHtml(hash) + '">Entire Office</label>';
        html += '<input type="radio" class="btn-check" name="ff-level-' + escapeHtml(hash) + '" id="ff-level-profile-' + escapeHtml(hash) + '" ' + (level === 'profile' ? 'checked' : '') + '>';
        html += '<label class="btn btn-outline-primary" for="ff-level-profile-' + escapeHtml(hash) + '">Per Profile</label>';
        html += '</div>';
        html += '</div>';
    }

    // Office-level toggles
    html += '<div id="ff-office-toggles-' + escapeHtml(hash) + '" class="' + (level === 'profile' ? 'hidden' : '') + '">';
    html += renderOfficeToggles(hash, analytics, flags, sections);
    html += '</div>';

    // Profile-level table
    if (profiles.length > 0 && office.profilesEnabled) {
        html += '<div id="ff-profile-toggles-' + escapeHtml(hash) + '" class="' + (level !== 'profile' ? 'hidden' : '') + '">';
        html += renderProfileTable(hash, profiles, profileFlags, flags);
        html += '</div>';
    }

    // Save status
    html += '<div class="mt-2 small" id="ff-status-' + escapeHtml(hash) + '"></div>';

    html += '</div></div>';
    return html;
}

function renderOfficeToggles(hash, analytics, flags, sections) {
    const simpleFlags = getSimpleFlags(flags);

    let html = '<div class="row g-3">';
    html += '<div class="col-md-4">';
    html += '<div class="form-check form-switch">';
    html += '<input class="form-check-input" type="checkbox" id="ff-admin-' + escapeHtml(hash) + '" ' + (analytics.enabledForOfficeAdmin ? 'checked' : '') + '>';
    html += '<label class="form-check-label" for="ff-admin-' + escapeHtml(hash) + '">Analytics (Office Admin)</label>';
    html += '</div></div>';
    html += '<div class="col-md-4">';
    html += '<div class="form-check form-switch">';
    html += '<input class="form-check-input" type="checkbox" id="ff-doctor-' + escapeHtml(hash) + '" ' + (analytics.enabledForDoctor ? 'checked' : '') + '>';
    html += '<label class="form-check-label" for="ff-doctor-' + escapeHtml(hash) + '">Analytics (Doctor)</label>';
    html += '</div></div>';

    // Render all simple flags dynamically
    simpleFlags.forEach(function(f) {
        html += '<div class="col-md-4">';
        html += '<div class="form-check form-switch">';
        html += '<input class="form-check-input ff-simple-toggle" type="checkbox" data-flag="' + escapeHtml(f.key) + '" data-hash="' + escapeHtml(hash) + '" id="ff-simple-' + escapeHtml(f.key) + '-' + escapeHtml(hash) + '" ' + (f.enabled ? 'checked' : '') + '>';
        html += '<label class="form-check-label" for="ff-simple-' + escapeHtml(f.key) + '-' + escapeHtml(hash) + '">' + escapeHtml(getFlagLabel(f.key)) + '</label>';
        html += '</div></div>';
    });
    html += '</div>';

    // Sections (collapsible)
    html += '<details class="mt-3">';
    html += '<summary class="text-muted small">Analytics Sections</summary>';
    html += '<div class="row g-2 mt-1">';
    SECTION_KEYS.forEach(s => {
        const checked = sections[s.key] !== false;
        html += '<div class="col-sm-6 col-md-4">';
        html += '<div class="form-check">';
        html += '<input class="form-check-input ff-section-cb" type="checkbox" id="ff-sec-' + escapeHtml(s.key) + '-' + escapeHtml(hash) + '" data-section="' + escapeHtml(s.key) + '" ' + (checked ? 'checked' : '') + '>';
        html += '<label class="form-check-label small" for="ff-sec-' + escapeHtml(s.key) + '-' + escapeHtml(hash) + '">' + escapeHtml(s.label) + '</label>';
        html += '</div></div>';
    });
    html += '</div></details>';
    return html;
}

function renderProfileTable(hash, profiles, profileFlags, officeFlags) {
    const analytics = officeFlags.analytics || {};
    const simpleFlags = getSimpleFlags(officeFlags);

    let html = '<div class="table-responsive"><table class="table table-sm table-hover">';
    html += '<thead><tr>';
    html += '<th>Profile</th><th>Role</th><th>Analytics Admin</th><th>Analytics Doctor</th>';
    // Dynamic simple flag columns
    simpleFlags.forEach(function(f) {
        html += '<th>' + escapeHtml(getFlagLabel(f.key)) + '</th>';
    });
    html += '</tr></thead><tbody>';

    profiles.forEach(p => {
        const pFlags = profileFlags[p.profileId] || {};
        const pAnalytics = pFlags.analytics || analytics;
        const pid = p.profileId;

        html += '<tr>';
        html += '<td>' + escapeHtml(p.displayName || 'Unnamed') + '</td>';
        html += '<td><span class="badge bg-light text-dark">' + escapeHtml(p.role || 'unknown') + '</span></td>';
        html += '<td><div class="form-check form-switch"><input class="form-check-input ff-profile-toggle" type="checkbox" data-hash="' + escapeHtml(hash) + '" data-profile="' + escapeHtml(pid) + '" data-flag="enabledForOfficeAdmin" ' + (pAnalytics.enabledForOfficeAdmin ? 'checked' : '') + '></div></td>';
        html += '<td><div class="form-check form-switch"><input class="form-check-input ff-profile-toggle" type="checkbox" data-hash="' + escapeHtml(hash) + '" data-profile="' + escapeHtml(pid) + '" data-flag="enabledForDoctor" ' + (pAnalytics.enabledForDoctor ? 'checked' : '') + '></div></td>';

        // Dynamic simple flag columns
        simpleFlags.forEach(function(f) {
            const pFlagVal = pFlags[f.key] || officeFlags[f.key] || {};
            html += '<td><div class="form-check form-switch"><input class="form-check-input ff-profile-toggle" type="checkbox" data-hash="' + escapeHtml(hash) + '" data-profile="' + escapeHtml(pid) + '" data-flag="' + escapeHtml(f.key) + '" ' + (pFlagVal.enabled ? 'checked' : '') + '></div></td>';
        });

        html += '</tr>';
    });

    html += '</tbody></table></div>';
    return html;
}

function wireOfficeCardEvents(office) {
    const hash = office.emailHash;

    // Level radio buttons
    const officeRadio = document.getElementById('ff-level-office-' + hash);
    const profileRadio = document.getElementById('ff-level-profile-' + hash);
    if (officeRadio) officeRadio.addEventListener('change', () => switchLevel(hash, 'office'));
    if (profileRadio) profileRadio.addEventListener('change', () => switchLevel(hash, 'profile'));

    // Office-level toggles (analytics)
    const adminToggle = document.getElementById('ff-admin-' + hash);
    const doctorToggle = document.getElementById('ff-doctor-' + hash);
    if (adminToggle) adminToggle.addEventListener('change', () => saveOfficeFlags(hash));
    if (doctorToggle) doctorToggle.addEventListener('change', () => saveOfficeFlags(hash));

    // Simple flag toggles (all dynamically rendered)
    const card = document.getElementById('ff-card-' + hash);
    if (card) {
        card.querySelectorAll('.ff-simple-toggle').forEach(toggle => {
            toggle.addEventListener('change', () => saveOfficeFlags(hash));
        });

        // Section checkboxes
        card.querySelectorAll('.ff-section-cb').forEach(cb => {
            cb.addEventListener('change', () => saveOfficeFlags(hash));
        });

        // Profile-level toggles
        card.querySelectorAll('.ff-profile-toggle').forEach(toggle => {
            toggle.addEventListener('change', () => saveProfileFlags(hash));
        });
    }
}

function switchLevel(hash, newLevel) {
    const officeToggles = document.getElementById('ff-office-toggles-' + hash);
    const profileToggles = document.getElementById('ff-profile-toggles-' + hash);

    if (newLevel === 'profile') {
        if (officeToggles) officeToggles.classList.add('hidden');
        if (profileToggles) profileToggles.classList.remove('hidden');
        saveProfileFlags(hash);
    } else {
        if (officeToggles) officeToggles.classList.remove('hidden');
        if (profileToggles) profileToggles.classList.add('hidden');
        saveOfficeFlags(hash);
    }
}

function collectOfficeFlags(hash) {
    const adminToggle = document.getElementById('ff-admin-' + hash);
    const doctorToggle = document.getElementById('ff-doctor-' + hash);

    const sections = {};
    const card = document.getElementById('ff-card-' + hash);
    if (card) {
        card.querySelectorAll('.ff-section-cb').forEach(cb => {
            const key = cb.dataset.section;
            if (key) sections[key] = cb.checked;
        });
    }

    const flags = {
        analytics: {
            enabledForOfficeAdmin: adminToggle ? adminToggle.checked : false,
            enabledForDoctor: doctorToggle ? doctorToggle.checked : false,
            sections: sections
        }
    };

    // Collect all simple flags dynamically
    if (card) {
        card.querySelectorAll('#ff-office-toggles-' + hash + ' .ff-simple-toggle').forEach(toggle => {
            const key = toggle.dataset.flag;
            if (key) {
                flags[key] = { enabled: toggle.checked };
            }
        });
    }

    return flags;
}

function collectProfileFlags(hash) {
    const card = document.getElementById('ff-card-' + hash);
    if (!card) return {};

    const profileFlags = {};
    card.querySelectorAll('.ff-profile-toggle').forEach(toggle => {
        const pid = toggle.dataset.profile;
        const flag = toggle.dataset.flag;
        if (!pid || !flag) return;

        if (!profileFlags[pid]) {
            profileFlags[pid] = { analytics: { enabledForOfficeAdmin: false, enabledForDoctor: false, sections: {} } };
        }

        if (flag === 'enabledForOfficeAdmin') {
            profileFlags[pid].analytics.enabledForOfficeAdmin = toggle.checked;
        } else if (flag === 'enabledForDoctor') {
            profileFlags[pid].analytics.enabledForDoctor = toggle.checked;
        } else {
            // Simple flag — collect dynamically
            profileFlags[pid][flag] = { enabled: toggle.checked };
        }
    });

    return profileFlags;
}

async function saveOfficeFlags(hash) {
    const flags = collectOfficeFlags(hash);
    const body = { level: 'office', flags: flags };
    await saveFlagsToBackend(hash, body);
}

async function saveProfileFlags(hash) {
    const profileFlags = collectProfileFlags(hash);
    const body = { level: 'profile', profileFlags: profileFlags };
    await saveFlagsToBackend(hash, body);
}

async function saveFlagsToBackend(hash, body) {
    const statusEl = document.getElementById('ff-status-' + hash);
    const badge = document.getElementById('ff-badge-' + hash);

    if (statusEl) {
        statusEl.textContent = 'Saving...';
        statusEl.className = 'mt-2 small text-primary';
    }

    // Clear previous fade timer
    if (ffSaveTimers[hash]) {
        clearTimeout(ffSaveTimers[hash]);
        ffSaveTimers[hash] = null;
    }

    try {
        const res = await apiFetchWithRefresh('/admin/feature-flags/office/' + encodeURIComponent(hash), {
            method: 'PUT',
            body: JSON.stringify(body),
            headers: { 'Content-Type': 'application/json' }
        });

        if (!res.ok) {
            const errData = await res.json().catch(() => ({}));
            throw new Error(errData.error || 'Save failed');
        }

        const data = await res.json();

        // Update badge
        const newLevel = data.level || body.level;
        if (badge) {
            badge.className = 'badge ' + getLevelBadgeClass(newLevel);
            badge.textContent = getLevelLabel(newLevel);
        }

        // Show auto-promoted notice
        if (data.autoPromoted && statusEl) {
            statusEl.textContent = '\u2713 Saved (auto-promoted to office level \u2014 all profiles had same flags)';
            statusEl.className = 'mt-2 small text-success';
        } else if (statusEl) {
            statusEl.textContent = '\u2713 Saved';
            statusEl.className = 'mt-2 small text-success';
        }

        // Fade status after 3s
        ffSaveTimers[hash] = setTimeout(() => {
            if (statusEl) { statusEl.textContent = ''; statusEl.className = 'mt-2 small'; }
        }, 3000);

        logger.info('FEATURE_FLAGS', 'Flags saved for office', { emailHash: hash, level: newLevel });
    } catch (err) {
        logger.error('FEATURE_FLAGS', 'Failed to save flags', err, { emailHash: hash });
        if (statusEl) {
            statusEl.textContent = '\u2717 Failed: ' + (err.message || 'Unknown error');
            statusEl.className = 'mt-2 small text-danger';
        }
        // Revert: re-fetch and re-render this office
        showToast('Failed to save feature flags: ' + (err.message || 'Unknown error'), 'error');
    }
}
